import { CompanyOperationalStatus, FeatureFlags } from '../exports/Constants';

/**
 * Pure predicates/lists for the provider-scoped `plg_onboarding` feature flag
 * (self-serve partner onboarding). Everything that is hidden for such
 * partners hangs off these helpers so existing providers stay untouched.
 * See tests/services/tenant/plgOnboarding.test.js.
 */

/**
 * Whether the acting service provider is on self-serve (PLG) onboarding. Reads
 * the flag list the backend appends as `enabled_feature_flags`; a missing
 * payload means "not PLG" so the classic dashboard is the fail-safe.
 *
 * Never the master, whatever its flags say — the backend's
 * ServiceProvider::runsPlgOnboarding() excludes it, so the two must agree on
 * which pages exist and which requests are refused.
 */
export function isPlgOnboardingProvider(serviceProvider) {
  return (
    serviceProvider?.is_master !== true &&
    Boolean(serviceProvider?.enabled_feature_flags?.includes(FeatureFlags.PlgOnboarding))
  );
}

/**
 * Whether Website Studio (v1 and v2) exists for the acting provider: FoodAmigos
 * (the master) only, never a white-label partner. The backend decides
 * (`website_studio_available`, ServiceProvider::offersWebsiteStudio()); a payload
 * without that field falls back to the same rule.
 */
export function isWebsiteStudioAvailable(serviceProvider) {
  if (typeof serviceProvider?.website_studio_available === 'boolean') {
    return serviceProvider.website_studio_available;
  }

  return serviceProvider?.is_master === true;
}

/**
 * Whether a PLG partner restaurant has yet to launch for the first time. Its first
 * launch belongs to the wizard's Go Live step, which checks every step before it,
 * so the "Live orders" toggle sends it there instead. `live_from` rather than the
 * live status: a restaurant paused after launching keeps resuming from the toggle.
 */
export function isInFirstLaunchOnboarding(serviceProvider, company) {
  return (
    isPlgOnboardingProvider(serviceProvider) &&
    !company?.live_from &&
    company?.operational_status !== CompanyOperationalStatus.Live
  );
}

/** Card keys of the provider-level Home -> General tab hidden for PLG partners (empty for them). */
export const PLG_HIDDEN_GENERAL_METRICS = [
  'net_billed_revenue',
  'gross_cash_income',
  'net_cash_income',
  'manual_connect_transfers',
  'processing_fees',
];

/** Stats fields those General cards request; dropped from the query so the backend isn't asked for them. */
export const PLG_HIDDEN_GENERAL_METRIC_FIELDS = [
  ...['net_billed_revenue', 'gross_cash_income', 'net_cash_income'].flatMap((prefix) => [
    prefix,
    `${prefix}_subscription`,
    `${prefix}_service_fee`,
    `${prefix}_external_delivery_platform_fee`,
    `${prefix}_other`,
    `${prefix}_external_delivery_passthrough_fee`,
    `${prefix}_gift_cards`,
    `${prefix}_platform_processing_fee`,
  ]),
  'manual_connect_transfers',
  'processing_fees',
  'processing_fees_stripe',
  'processing_fees_paypal',
  'platform_processing_fees',
];

/** Card keys of the provider-level Home -> Restaurant details tab hidden for PLG partners. */
export const PLG_HIDDEN_RESTAURANT_DETAILS_METRICS = [
  'new_pricing_adoption_rate',
  'mobile_app_attach_rate',
  'mobile_app_setup_fee_breakdown',
  'storefront',
];

/** Stats fields those Restaurant-details cards request (`companies` stays: other cards use it). */
export const PLG_HIDDEN_RESTAURANT_DETAILS_FIELDS = [
  'new_generation_plan_subscriptions_count',
  'all_plan_subscriptions_count',
  'new_generation_plan_adoption_rate',
  'mobile_app_subscriptions_count',
  'mobile_app_attach_rate',
  'mobile_app_setup_fee_breakdown',
  'has_storefront',
  'has_storefront_percentage',
];

/** Returns `fields` without the PLG-hidden ones when `isPlg`, otherwise unchanged. */
export function excludePlgHiddenFields(fields, hiddenFields, isPlg) {
  return isPlg ? fields.filter((field) => !hiddenFields.includes(field)) : fields;
}

/** Whether the metric card `key` is hidden for the acting provider. */
export function isPlgHiddenMetric(key, hiddenKeys, isPlg) {
  return isPlg && hiddenKeys.includes(key);
}

/**
 * Whether the "Franchise sync" nav item is hidden for the current selection.
 *
 * Franchise sync only means something for a brand that actually has more than
 * one location, and a partner looking at a standalone restaurant has no context
 * to judge whether they should be setting one up — they ask us or guess, and
 * both cost time (Artur's launch-checklist feedback §7).
 *
 * Scoped to partners on the new onboarding flow only: providers still on the
 * classic mechanism keep the behaviour they have today, so nothing changes under
 * partners who were never shown the new flow.
 *
 * The permission and franchise-selection gates are unchanged and apply to
 * everyone.
 */
export function isFranchiseSyncHidden({
  hasSelectedFranchise,
  canWriteMasterMenu,
  isPlgPartner,
  isMultiLocationBrand,
}) {
  if (!hasSelectedFranchise || !canWriteMasterMenu) {
    return true;
  }

  return Boolean(isPlgPartner) && !isMultiLocationBrand;
}
